/**
 * The shape a renderer needs to draw one configured field, and the adapter that
 * flattens the unified configuration endpoint's rows into it.
 *
 * Kept free of the API layer on purpose: importing `services/api/members` runs
 * module-scope code that touches localStorage, which cannot load in a unit test.
 * `hooks/useConfiguredFields` does the fetching and re-exports these.
 */

import type { ProfileField, VisibilityRules } from '@/services/api/members';
import { normalizeOptions, type NormalizedOption } from '@/components/forms/fieldOptions';

/** A field flattened into what a renderer actually needs to draw one input. */
export interface ConfiguredField {
  /** The key values are stored under — a custom field's name, a standard field's key. */
  key: string;
  label: string;
  type: string;
  required: boolean;
  readOnly: boolean;
  /** The admin's configured width; drives whether the input spans the grid. */
  fullWidth: boolean;
  options: NormalizedOption[];
  visibilityRules?: VisibilityRules | null;
  /** True for a standard (system-default) field, false for a custom one. */
  isStandard: boolean;
  /** The admin hid this field. Only meaningful when fetched with include_hidden. */
  hidden: boolean;
  helpText?: string;
  placeholder?: string;
  categoryKey: string;
  categoryName: string;
}

export function toConfiguredField(f: ProfileField): ConfiguredField {
  return {
    key: f.field_key,
    label: f.display_name,
    type: f.field_type,
    required: !!f.is_required,
    readOnly: !!f.is_readonly,
    fullWidth: f.display_width === 'full' || f.field_type === 'long_text',
    // Options arrive in two shapes in production (bare string[] and
    // {label,value}[]); normalizeOptions is the shared reconciler.
    options: normalizeOptions((f.field_options as any) ?? null),
    visibilityRules: f.visibility_rules ?? null,
    isStandard: !!f.is_core_field,
    hidden: !!f.is_hidden,
    helpText: f.help_text,
    placeholder: f.placeholder_text,
    categoryKey: f.category_key,
    categoryName: f.category_name,
  };
}
